import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Camera, Video, Sparkles } from "lucide-react";
import { useLang } from "./LangContext";

const packIcons = [Camera, Video, Sparkles];

export default function Packs() {
  const { t } = useLang();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="tarifs" data-testid="packs-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">
            {t.packs.label}
          </span>
          <h2
            data-testid="packs-title"
            className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900"
            style={{ fontFamily: 'Outfit' }}
          >
            {t.packs.title}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {t.packs.items.map((pack, i) => {
            const Icon = packIcons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.15, type: "spring", stiffness: 120 }}
                data-testid={`pack-card-${i}`}
                className={`relative rounded-2xl p-8 border transition-all duration-300 hover:shadow-xl group ${
                  pack.popular
                    ? "border-[#73B5E2] bg-[#73B5E2]/[0.03] shadow-lg scale-[1.02]"
                    : "border-slate-200 bg-white hover:border-[#73B5E2]/40"
                }`}
              >
                {pack.popular && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.6, type: "spring" }}
                    className="absolute -top-3 left-1/2 -translate-x-1/2"
                  >
                    <span className="bg-[#73B5E2] text-white text-xs font-semibold px-4 py-1 rounded-full shadow-md">
                      {t.packs.popular}
                    </span>
                  </motion.div>
                )}

                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    pack.popular ? "bg-[#73B5E2]/15" : "bg-slate-100"
                  }`}>
                    <Icon className={`w-5 h-5 ${pack.popular ? "text-[#73B5E2]" : "text-slate-500"}`} />
                  </div>
                  <h3
                    className="text-lg font-semibold text-slate-900"
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {pack.name}
                  </h3>
                </div>

                <div className="mb-5">
                  <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    {pack.price}
                  </span>
                  <span className="text-slate-500 text-sm ml-1">CHF</span>
                </div>

                <p className="text-slate-500 text-sm mb-6">{pack.desc}</p>

                <ul className="space-y-3 mb-8">
                  {pack.features.map((feature, j) => (
                    <motion.li
                      key={j}
                      initial={{ opacity: 0, x: -10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.4 + i * 0.1 + j * 0.05 }}
                      className="flex items-center gap-2.5 text-sm text-slate-600"
                    >
                      <Check className="w-4 h-4 text-[#73B5E2] flex-shrink-0" />
                      {feature}
                    </motion.li>
                  ))}
                </ul>

                <button
                  onClick={scrollToContact}
                  data-testid={`pack-cta-${i}`}
                  className="w-full py-3 rounded-full text-sm font-medium transition-all hover:scale-[1.02] bg-[#73B5E2] text-white hover:bg-[#5AA0D0] shadow-lg shadow-blue-500/20"
                >
                  {t.packs.cta}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
